import React from 'react';
import Icon from '../ui/Icon';
import { BEHAVIOUR_META } from './diagnosticHelpers';

// Sections 5 & 6 — Strength Analysis and Improvement Areas.
// Both are derived from the metrics/topics already returned by the API (no fabrication).

const buildStrengths = (apiStrengths, metrics, topics) => {
  const out = [...(apiStrengths || [])];
  // Supplement with metric- and topic-supported strengths.
  Object.entries(BEHAVIOUR_META).forEach(([code, meta]) => {
    if (Number(metrics?.[code]?.score) >= 80) out.push(meta.strong);
  });
  (topics || []).filter(t => t.accuracy >= 80).slice(0, 3)
    .forEach(t => out.push(`Strong grasp of ${t.topic_name} (${t.accuracy}%)`));
  // De-duplicate, keep order.
  return [...new Set(out)];
};

const buildImprovements = (metrics, topics) => {
  const items = [];
  Object.entries(BEHAVIOUR_META).forEach(([code, meta]) => {
    const s = Number(metrics?.[code]?.score);
    if (Number.isFinite(s) && s < 60) {
      items.push({
        area: meta.label,
        reason: meta.meaning,
        impact: 'Can lower performance under real assessment conditions.',
        suggestion: meta.improve
      });
    }
  });
  (topics || []).filter(t => t.accuracy < 50).slice(0, 4).forEach(t => {
    items.push({
      area: t.topic_name,
      reason: `Low accuracy in this topic (${t.accuracy}%).`,
      impact: 'Weak topic mastery pulls down overall knowledge scores.',
      suggestion: 'Revise the core concepts and practise targeted questions.'
    });
  });
  return items;
};

const StrengthsImprovements = ({ strengths, metrics, topics }) => {
  const strengthItems = buildStrengths(strengths, metrics, topics);
  const improvementItems = buildImprovements(metrics, topics);

  return (
    <div className="dxr-si-grid">
      {/* Section 5 — Strengths */}
      <div className="dxr-card">
        <h3 className="dxr-card-title">Strength Analysis</h3>
        {strengthItems.length > 0 ? (
          <ul className="dxr-strength-list">
            {strengthItems.map((s, i) => (
              <li key={i}><span className="dxr-check" aria-hidden><Icon name="check" size={14} /></span>{s}</li>
            ))}
          </ul>
        ) : (
          <p className="dxr-muted">Complete more assessments to surface consistent strengths.</p>
        )}
      </div>

      {/* Section 6 — Improvement Areas */}
      <div className="dxr-card">
        <h3 className="dxr-card-title">Areas to Improve</h3>
        {improvementItems.length > 0 ? (
          <div className="dxr-improve-list">
            {improvementItems.map((it, i) => (
              <div className="dxr-improve-item" key={i}>
                <div className="dxr-improve-area">{it.area}</div>
                <div className="dxr-improve-line"><span>Reason</span>{it.reason}</div>
                <div className="dxr-improve-line"><span>Impact</span>{it.impact}</div>
                <div className="dxr-improve-line"><span>Suggestion</span>{it.suggestion}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="dxr-muted">No critical improvement areas detected — keep maintaining consistent performance.</p>
        )}
      </div>
    </div>
  );
};

export default StrengthsImprovements;
